/**
 * MessageBubble (Sprint 7 B5).
 *
 * Render 1 message:
 *   - user: bubble màu xanh nhạt right-aligned + Markdown (câu hỏi soạn bằng rich editor)
 *   - assistant: bubble màu xám left-aligned + Markdown content + Citations footer
 *   - system: hidden (chỉ debug)
 */

import { Avatar, Space, Tag, Typography } from "antd";
import { RobotOutlined, UserOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { MarkdownContent } from "@/components/MarkdownContent";
import type { Citation, MessageRole } from "@/api/learning";

const { Text } = Typography;

interface Props {
  role: MessageRole;
  content: string;
  citations?: Citation[];
  streaming?: boolean;
}

export function MessageBubble({ role, content, citations = [], streaming = false }: Props) {
  if (role === "system") return null;

  const isUser = role === "user";
  return (
    <div
      data-testid="message-bubble"
      data-role={role}
      style={{
        display: "flex",
        gap: 8,
        marginBottom: 12,
        flexDirection: isUser ? "row-reverse" : "row",
      }}
    >
      <Avatar
        size="small"
        icon={isUser ? <UserOutlined /> : <RobotOutlined />}
        style={{ background: isUser ? "#1677ff" : "#722ed1", flexShrink: 0 }}
      />
      <div
        style={{
          maxWidth: "78%",
          padding: "8px 12px",
          borderRadius: 8,
          background: isUser ? "#e6f4ff" : "#f5f5f5",
          wordBreak: "break-word",
        }}
      >
        {isUser ? (
          // `breaks`: tin cũ gõ ở textarea (Shift+Enter) chỉ xuống dòng đơn.
          <MarkdownContent source={content} breaks />
        ) : (
          <>
            {content ? (
              <MarkdownContent source={content} />
            ) : streaming ? (
              <Text type="secondary" italic>
                Đang nghĩ...
              </Text>
            ) : null}
            {streaming && content && (
              <span data-testid="streaming-cursor" style={{ marginLeft: 4 }}>
                ▌
              </span>
            )}
            {citations.length > 0 && (
              <div
                data-testid="citations"
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: "1px dashed #d9d9d9",
                }}
              >
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Trích dẫn:
                </Text>
                <Space wrap size={[4, 4]} style={{ marginTop: 4 }}>
                  {citations.map((c) => (
                    <Link
                      key={`${c.lesson_id}-${c.chunk_order}`}
                      // Sprint 13 B2 — Pass ?highlight=<chunk_order> để LessonDetail
                      // scroll + flash chunk được trích dẫn.
                      to={`/lessons/${c.lesson_slug}?highlight=${c.chunk_order}`}
                    >
                      <Tag color="purple" style={{ cursor: "pointer" }}>
                        {c.lesson_title}
                        <span style={{ opacity: 0.7, marginLeft: 4, fontSize: 10 }}>
                          §{c.chunk_order + 1}
                        </span>
                      </Tag>
                    </Link>
                  ))}
                </Space>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
